// W26 (2026-10-20): "Recent activity" card for a REAL contact on the
// Current Action screen. Replaces the design-fixture ConversationCard
// (Sarah's leaking-bottle thread) whenever the session is real.
//
// Renders:
//   - the latest note or call from GET /v1/contacts/:id/timeline as the
//     approved "latest" bubble (type icon, date, first ~200 chars of
//     body), with the earlier items behind the approved "Expand thread
//     history" toggle (W40.1);
//   - the Text / Email / Call channel toggle + draft box the approved
//     layout already has. The draft opener is neutral and only
//     pre-filled when we actually know the customer's first name.
//
// W40.1 (2026-10-21, M5): the approved conversation controls are present
// on a real session, never hidden:
//   - "Create CS Ticket" (header) and "Send Reply" / "Save call notes"
//     (draft) are vendor writes (Zoho ticket, Kixie SMS, rep email,
//     HubSpot note). Both stay VISIBLE and inert with the shared
//     live-write gate copy (LIVE_WRITE_PAUSED_LABEL) in the draft helper
//     line and the button tooltip, the same rule the other screens use.
//     Before W40.1 "Send" was an enabled no-op, which is worse than an
//     honest inert control.
//   - "Expand thread history" is a read-only control (no vendor write),
//     so it is NOT labelled with the gate: it reveals the earlier
//     timeline items in place (aria-expanded, like the approved file)
//     and is disabled with an honest tooltip when there is nothing
//     earlier to show.
//
// Nothing here falls back to fixture text. Empty timeline -> honest
// empty state. Error -> the api-server error code, visible.
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { CollectionResult, TimelineItem } from '../../_shared/useContactCollections.js';
import { LIVE_WRITE_PAUSED_LABEL } from '../../_w2a_lower/liveWrite.js';

type Channel = 'Text' | 'Email' | 'Call';

export interface RecentActivityCardProps {
  customerFirstName: string;
  repFirstName: string | null;
  horseName: string | null;
  timeline: CollectionResult<TimelineItem>;
  /** True on a real session: Create CS Ticket and Send stay visible but inert. */
  liveWritesPaused?: boolean;
}

const BODY_PREVIEW_CHARS = 200;

export const EXPAND_THREAD_LABEL = 'Expand thread history';
export const COLLAPSE_THREAD_LABEL = 'Collapse thread history';
export const NO_EARLIER_ITEMS_TITLE = 'No earlier notes or calls on file';

export function formatTimelineDate(iso: string | null): string {
  if (!iso) return 'Date unknown';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return 'Date unknown';
  const d = new Date(ms);
  // en-US short date + time; the rep's browser locale/zone applies.
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function previewBody(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  if (flat.length <= BODY_PREVIEW_CHARS) return flat;
  return flat.slice(0, BODY_PREVIEW_CHARS).trimEnd() + '…';
}

/**
 * Neutral opener. Only produced when the customer's first name is
 * known; the horse clause only when a horse name is on file.
 */
export function buildOpener(
  customerFirstName: string,
  repFirstName: string | null,
  horseName: string | null,
): string {
  const first = customerFirstName.trim();
  if (!first) return '';
  const who = repFirstName && repFirstName.trim() ? `this is ${repFirstName.trim()} from 100X Equine` : 'this is 100X Equine';
  const about = horseName && horseName.trim() ? horseName.trim() : 'your horse';
  return `Hi ${first}, ${who}, checking in on ${about}…`;
}

function typeLabel(item: TimelineItem): string {
  return item.type === 'call' ? 'Call' : 'Note';
}

/** Eyebrow over the latest item: "LATEST NOTE" / "LATEST CALL", or a neutral label. */
export function latestLabel(latest: TimelineItem | null): string {
  return latest ? `LATEST ${typeLabel(latest).toUpperCase()}` : 'RECENT ACTIVITY';
}

function TimelineEntry({ item }: { item: TimelineItem }): ReactNode {
  return (
    <>
      <div className="message-meta">
        <strong>
          <Icon id={item.type === 'call' ? 'message' : 'work'} />{' '}
          {typeLabel(item)}
          {item.title ? ` · ${item.title}` : ''}
        </strong>
        <span>{formatTimelineDate(item.at)}</span>
      </div>
      <div className="bubble">{previewBody(item.body) || '(no text)'}</div>
    </>
  );
}

export function RecentActivityCard(props: RecentActivityCardProps): ReactNode {
  const { customerFirstName, repFirstName, horseName, timeline, liveWritesPaused = false } = props;
  const [activeChannel, setActiveChannel] = useState<Channel>('Call');
  const [expanded, setExpanded] = useState(false);
  const opener = buildOpener(customerFirstName, repFirstName, horseName);
  const [drafts, setDrafts] = useState<Record<Channel, string>>({
    Text: opener,
    Email: opener,
    Call: '',
  });
  // Re-seed the opener once the profile / horses resolve, but never
  // overwrite text the rep already typed.
  useEffect(() => {
    setDrafts((prev) => ({
      Text: prev.Text.length === 0 ? opener : prev.Text,
      Email: prev.Email.length === 0 ? opener : prev.Email,
      Call: prev.Call,
    }));
  }, [opener]);

  const draftLabel = activeChannel === 'Call' ? 'Call notes' : 'Draft message';
  const draftHint = activeChannel === 'Call' ? 'Capture what they are feeding today' : 'Review before sending';
  // Approved labels: "Send Reply" on Text / Email (01 v7 `#send-label`),
  // "Save call notes" on Call.
  const sendLabel = activeChannel === 'Call' ? 'Save call notes' : 'Send Reply';
  const channelLabel =
    activeChannel === 'Call' ? 'Phone call' : activeChannel === 'Email' ? 'Email' : 'Text message';

  // The timeline arrives newest first (W26): the first item is the
  // approved "latest" bubble, the rest sit behind the expand toggle.
  const items = timeline.status === 'ready' ? timeline.items : [];
  const latest = items[0] ?? null;
  const earlier = items.slice(1);
  const canExpand = earlier.length > 0;
  const expandTitle =
    timeline.status === 'loading'
      ? 'Loading notes and calls'
      : timeline.status === 'error'
        ? 'Notes and calls could not load'
        : canExpand
          ? undefined
          : NO_EARLIER_ITEMS_TITLE;
  const gateNoteId = 'cx-write-gate-note';

  return (
    <section className="card conversation" aria-label="Recent activity" data-testid="recent-activity-card">
      <div className="card-head">
        <div className="row">
          <span className="head-icon">
            <Icon id="clock" />
          </span>
          <h3>Recent activity</h3>
        </div>
        <div className="channel-preview">
          <span>Reply channel</span>
          <div role="group" aria-label="Reply channel">
            {(['Text', 'Email', 'Call'] as Channel[]).map((channel) => (
              <button
                key={channel}
                type="button"
                aria-pressed={activeChannel === channel}
                onClick={() => setActiveChannel(channel)}
              >
                {channel}
              </button>
            ))}
          </div>
        </div>
        <div className="conversation-header-actions">
          <span className="small" id="channel-label">
            {channelLabel}
          </span>
          <button
            type="button"
            className="cs-inline"
            id="get-cs-help"
            disabled={liveWritesPaused}
            aria-disabled={liveWritesPaused || undefined}
            aria-describedby={liveWritesPaused ? gateNoteId : undefined}
            data-live-write-paused={liveWritesPaused || undefined}
            title={liveWritesPaused ? `Create CS Ticket: ${LIVE_WRITE_PAUSED_LABEL}` : undefined}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 13v-1a8 8 0 0 1 16 0v1M4 12H3v7h4v-7H4Zm16 0h1v7h-4v-7h3ZM20 19c0 2-3 3-7 3" />
            </svg>
            Create CS Ticket
          </button>
        </div>
      </div>
      <div className="thread" data-testid="recent-activity-list" aria-busy={timeline.status === 'loading'}>
        <div className="cx-thread-toolbar">
          <span className="eyebrow" id="cx-current-latest-label">{latestLabel(latest)}</span>
          <button
            className="cx-thread-toggle"
            type="button"
            id="cx-current-thread-expand"
            aria-expanded={expanded}
            aria-controls="cx-current-thread-history"
            disabled={!canExpand}
            title={expandTitle}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? COLLAPSE_THREAD_LABEL : EXPAND_THREAD_LABEL}
          </button>
        </div>
        <ol
          className="cx-thread-history"
          id="cx-current-thread-history"
          aria-label="Earlier notes and calls"
          tabIndex={0}
          hidden={!expanded || !canExpand}
          data-testid="recent-activity-history"
        >
          {expanded
            ? earlier.map((item, idx) => (
                <li key={item.id ?? `${item.type}-${idx + 1}`} data-testid="recent-activity-item">
                  <TimelineEntry item={item} />
                </li>
              ))
            : null}
        </ol>
        {timeline.status === 'loading' ? (
          <p className="muted" role="status">Loading notes and calls…</p>
        ) : timeline.status === 'error' ? (
          <p className="muted" role="alert">
            Notes and calls could not load ({timeline.errorCode || 'ERR'}).
          </p>
        ) : timeline.status === 'ready' && !latest ? (
          <p className="muted" data-testid="recent-activity-empty">
            No notes or calls on file yet. Log the first one after this call.
          </p>
        ) : latest ? (
          <div data-testid="recent-activity-item" className="cx-latest-entry">
            <TimelineEntry item={latest} />
          </div>
        ) : null}
      </div>
      <div className="draft">
        <div className="draft-top">
          <strong id="draft-label">{draftLabel}</strong>
          <span id="draft-hint">{draftHint}</span>
        </div>
        <textarea
          id="draft"
          aria-label="Draft message or call notes"
          value={drafts[activeChannel]}
          onChange={(e) => setDrafts((prev) => ({ ...prev, [activeChannel]: e.target.value }))}
        />
        <div className="draft-bottom">
          <span className="draft-helper" id={gateNoteId}>
            {liveWritesPaused ? LIVE_WRITE_PAUSED_LABEL : 'Edit before you send.'}
          </span>
          <button
            id="send"
            className="primary"
            type="button"
            disabled={liveWritesPaused}
            aria-disabled={liveWritesPaused || undefined}
            aria-describedby={liveWritesPaused ? gateNoteId : undefined}
            data-live-write-paused={liveWritesPaused || undefined}
            title={liveWritesPaused ? `${sendLabel}: ${LIVE_WRITE_PAUSED_LABEL}` : undefined}
            onClick={() => {
              // TODO(runtime): dispatch to KIXIE_SEND_SMS / rep-email /
              // call-evidence depending on activeChannel once gates are
              // flipped on.
            }}
          >
            <Icon id="icon-send" />
            <span id="send-label">{sendLabel}</span>
          </button>
        </div>
      </div>
      <div className="need">
        <Icon id="check" />
        <div>
          <strong>Confirm what they are feeding today.</strong>
          <p>Log the call before you move to the next customer.</p>
        </div>
      </div>
    </section>
  );
}
